import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import Select from "react-select";
import axiosInstance from "../../../../../services/AxiosInstance";

const MedicineDrop = ({
  value,
  onChange,
  isMulti = false,
  medicineTypeFilter = null, // "Internal" | "External" | null
  excludeMedicineIds = [],
}) => {
  // All fetched medicines (accumulated across pages)
  const [allMedicines, setAllMedicines] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [selectedMedicineLabel, setSelectedMedicineLabel] = useState(null);

  const searchTimeoutRef = useRef(null);
  const isFetchingRef = useRef(false);
  const initialFetchRef = useRef(false);
  const pageSize = 10;

  // Apply client‑side filter whenever allMedicines or filter changes
  useEffect(() => {
    let filtered = [...allMedicines];
  
    // Filter Internal / External
    if (medicineTypeFilter === "Internal") {
      filtered = filtered.filter(
        (m) => m.medicine_int_ext_type !== "External"
      );
    } else if (medicineTypeFilter === "External") {
      filtered = filtered.filter(
        (m) => m.medicine_int_ext_type === "External"
      );
    }
  
    // Remove already selected medicines
    filtered = filtered.filter(
      (m) =>
        !excludeMedicineIds.includes(String(m.value))
    );
  
    setFilteredOptions(filtered);
  }, [
    allMedicines,
    medicineTypeFilter,
    excludeMedicineIds,
  ]);

  // Reset selected value when filter changes
  useEffect(() => {
    if (onChange) onChange(null);
  }, [medicineTypeFilter, onChange]);

  const fetchMedicines = useCallback(
    async (pageNo, searchText = "", isReset = false) => {
      if (isFetchingRef.current) return;

      try {
        isFetchingRef.current = true;
        if (isReset) {
          setLoading(true);
        } else {
          setIsLoadingMore(true);
        }

        const res = await axiosInstance.post("/admin/medicineList", {
          pagination: false,
          pageid: pageNo,
          search_by: "medicine_name",
          search_keyword: searchText || "",
        });

        const result = res.data;
        if (result?.status === "success") {
          const newData = result.data || [];
          const numRows = result.num_rows || 0;
          const totalPages = newData.length > 0 ? Math.ceil(numRows / pageSize) : 0;
          const currentPageIndex = pageNo;
          const hasMorePages = currentPageIndex < totalPages - 1;

          const formatted = newData.map((item) => ({
            value: String(item.medicine_id),
            label: item.medicine_name || item.display_name_en || `Medicine ${item.medicine_id}`,
            medicine_int_ext_type: item.medicine_int_ext_type,
            isExternal: item.medicine_int_ext_type === "External",
            unit_text: item.unit_text || "",
            dose_text: item.dose_text || "",
          }));

          if (isReset) {
            setAllMedicines(formatted);
            setPage(0);
          } else {
            setAllMedicines((prev) => {
              const existingIds = new Set(prev.map((opt) => opt.value));
              const uniqueNew = formatted.filter((opt) => !existingIds.has(opt.value));
              return [...prev, ...uniqueNew];
            });
            setPage(pageNo);
          }
          setHasMore(hasMorePages);
        }
      } catch (err) {
        console.error("[v0] Medicine fetch error:", err.message);
      } finally {
        isFetchingRef.current = false;
        if (isReset) {
          setLoading(false);
        } else {
          setIsLoadingMore(false);
        }
      }
    },
    []
  );

  const fetchMedicineLabel = useCallback(async (medicineId) => {
    try {
      const res = await axiosInstance.post("/admin/medicineList", {
        pagination: false,
        search_by: "medicine_id",
        search_keyword: String(medicineId),
      });
      if (res.data?.status === "success" && res.data?.data?.length > 0) {
        const medicine = res.data.data[0];
        const label = medicine.medicine_name || medicine.display_name_en || `Medicine ${medicineId}`;
        setSelectedMedicineLabel(label);
      }
    } catch (err) {
      console.error("[v0] Error fetching medicine label:", err.message);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (!initialFetchRef.current && !isInitialized) {
      initialFetchRef.current = true;
      fetchMedicines(0, "", true);
      setIsInitialized(true);
    }
  }, [fetchMedicines, isInitialized]);

  // Fetch label for edit mode (when value is passed without label)
  useEffect(() => {
    if (value) {
      const valueArray = Array.isArray(value) ? value : [value];
      const firstValue = valueArray[0];
      if (typeof firstValue === "string" || (firstValue?.value && !firstValue?.label)) {
        const medicineId = typeof firstValue === "string" ? firstValue : firstValue.value;
        fetchMedicineLabel(medicineId);
      }
    }
  }, [value, fetchMedicineLabel]);

  // Search with debounce
  const handleInputChange = useCallback(
    (inputValue) => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        setSearch(inputValue);
        setPage(0);
        fetchMedicines(0, inputValue, true);
      }, 500);
    },
    [fetchMedicines]
  );

  // Load more on scroll
  const handleMenuScrollToBottom = useCallback(() => {
    if (hasMore && !isLoadingMore && !loading && !isFetchingRef.current) {
      const nextPage = page + 1;
      fetchMedicines(nextPage, search, false);
    }
  }, [hasMore, isLoadingMore, loading, page, search, fetchMedicines]);

  // Display value – tries to find label from filteredOptions or stored label
  const selectedValue = useMemo(() => {
    if (!value) return isMulti ? [] : null;
    const valueArray = Array.isArray(value) ? value : [value];
    if (valueArray.length === 0) return isMulti ? [] : null;

    // Already has label
    if (valueArray[0]?.label && valueArray[0].label !== "") {
      return isMulti ? valueArray : valueArray[0];
    }

    // Edit mode with fetched label
    if (selectedMedicineLabel) {
      const medicineId = typeof valueArray[0] === "string" ? valueArray[0] : valueArray[0]?.value;
      const valueWithLabel = { value: medicineId, label: selectedMedicineLabel };
      return isMulti ? [valueWithLabel] : valueWithLabel;
    }

    // Look up from filteredOptions
    const selectedOptions = valueArray
      .map((v) => {
        const medicineId = typeof v === "string" ? v : v?.value;
        const foundOption = filteredOptions.find((opt) => opt.value === medicineId);
        return foundOption || { value: medicineId, label: "" };
      })
      .filter((opt) => opt && opt.value);

    return isMulti ? selectedOptions : selectedOptions[0] || null;
  }, [value, filteredOptions, isMulti, selectedMedicineLabel]);

  const handleChange = useCallback((selected) => {
    if (onChange) onChange(selected);
  }, [onChange]);

  // Custom option label with badge
  const formatOptionLabel = (option) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span>{option.label}</span>
      {option.isExternal && (
        <span
          style={{
            background: "#ff9800",
            color: "#fff",
            padding: "2px 6px",
            borderRadius: "12px",
            fontSize: "10px",
            marginLeft: "8px",
          }}
        >
          External
        </span>
      )}
    </div>
  );

  // Optional: disable internal medicines when filter === "External"
  const isOptionDisabled = (option) => {
    if (medicineTypeFilter === "External" && !option.isExternal) return true;
    return false;
  };

  const customStyles = {
    menu: (provided) => ({ ...provided, zIndex: 9999 }),
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  };

  return (
    <Select
      isMulti={isMulti}
      options={filteredOptions}
      value={selectedValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      placeholder="Search & Select Medicine"
      isLoading={loading}
      isSearchable
      isClearable
      onMenuScrollToBottom={handleMenuScrollToBottom}
      noOptionsMessage={() => (loading ? "Loading..." : "No medicines found")}
      formatOptionLabel={formatOptionLabel}
      getOptionLabel={(option) => option.label}
      getOptionValue={(option) => option.value}
      maxMenuHeight={300}
      styles={customStyles}
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      isOptionDisabled={isOptionDisabled}
    />
  );
};

export default MedicineDrop;